import { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';

import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import {
  resetStore,
  RootState,
  useAppDispatch,
  useAppSelector
} from '../../../../state/store';
import { logoutAsync } from '../../../../state/auth/authSlice';
import { cleanupZohoAsapOnPortalLogout } from '../../../../services/zohoAsap/zohoAsapClient';
import { useSelector } from 'react-redux';
import { REASON_LIST } from '../../../../utils/constantTypes';
import {
  cancelSubscriptionAsync,
  getSelectedSubscription,
  pauseSubscriptionCollectionAsync
} from '../../../../state/subscriptions/subscriptionSlice';
import {
  getAccessToken,
  getLoading,
  getLoggedInUserId,
  getLoggedInUsersSubscription
} from '../../../../state/user/userSlice';
// DEPRECATED: replaced by CancelSubscriptionFlow
// import CancelSubscriptionModal from '../../../../content/applications/Subscriptions/CancelSubscriptionModal';
import CancelSubscriptionFlow from '../../../../content/applications/Subscriptions/CancelSubscriptionFlow';
import { DATE_FORMATS, ROLES } from '../../../../constants';
import ProfileMenuComponent from './UserMenuOptions';
import { CancelSubscriptionFormData } from '../../../../content/applications/Subscriptions/CancelSubscriptionFlow/types';
import { setShowAlert } from '../../../../state/utilts/utilsSlice';
import { formatDate } from '../../../../utils';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

const CancelSubscriptionBtn = styled(Button)(({ theme }) => ({
  transition: 'ease'
}));

function HeaderUserbox() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const accessToken = useAppSelector(getAccessToken);
  const user = useSelector((state: RootState) => state.user.userData);
  const dispatch = useAppDispatch();
  const userId = useAppSelector(getLoggedInUserId);
  const userLoading = useAppSelector(getLoading('user'));
  const subsLoading = useAppSelector(getLoading('subscription'));
  const selectedSubscription = useAppSelector(getSelectedSubscription);
  const userSubscription = useAppSelector(getLoggedInUsersSubscription);
  // DEPRECATED: cancel form state now lives in CancelSubscriptionFlow.
  // const [cancelSubscriptionObj, setCancelSubscriptionObj] = useState({
  //   reason: REASON_LIST[0].key,
  //   comment: ''
  // });
  // DEPRECATED: replaced by isCancelFlowOpen.
  // const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
  //   useState(false);
  const [isCancelFlowOpen, setIsCancelFlowOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    void cleanupZohoAsapOnPortalLogout();
    await dispatch(logoutAsync(accessToken));
    navigate('/login');
    await dispatch(resetStore());
  };

  const handleCancelSubscriptionModalOpen = () => {
    handleClose();
    setIsCancelFlowOpen(true);
  };

  const handleCancelSubscription = async (
    cancelSubscriptionObj: CancelSubscriptionFormData
  ) => {
    const resultAction = await dispatch(
      cancelSubscriptionAsync({
        accessToken,
        data: { userId, ...cancelSubscriptionObj }
      })
    );

    if (cancelSubscriptionAsync.fulfilled.match(resultAction)) {
      setIsCancelFlowOpen(false);
      handleClose();
      return true;
    }

    return false;
  };

  const handleNavigateToTraining = () => {
    setIsCancelFlowOpen(false);
    handleClose();
    navigate('/training');
  };

  const handlePauseSubscription = async (payload: { resumeAt?: string }) => {
    const subscriptionId = userSubscription.subscriptionId;

    if (!subscriptionId) {
      dispatch(
        setShowAlert({
          show: true,
          message: 'Unable to pause subscription. Missing subscription ID.',
          type: 'error'
        })
      );
      return false;
    }

    const resultAction = await dispatch(
      pauseSubscriptionCollectionAsync({
        accessToken,
        subscriptionId,
        data: payload
      })
    );

    if (pauseSubscriptionCollectionAsync.fulfilled.match(resultAction)) {
      setIsCancelFlowOpen(false);
      handleClose();
      return true;
    }

    return false;
  };
  // DEPRECATED: handled inside CancelSubscriptionFlow.
  // const handleCancelSubscriptionReason = (e) => {
  //   const field = e.target.name;
  //   const fieldValue = e.target.value;
  //   setCancelSubscriptionObj((prev) => ({ ...prev, [field]: fieldValue }));
  // };

  // DEPRECATED: handled inside CancelSubscriptionFlow.
  // const handleCancelSubscriptionReason = (e) => {
  //   const field = e.target.name;
  //   const fieldValue = e.target.value;
  //   setCancelSubscriptionObj((prev) => ({ ...prev, [field]: fieldValue }));
  // };

  const isButtonDisabled = userLoading || subsLoading;

  const hasSubscription = userSubscription._id;
  const hasCancelled = userSubscription.isCancelled;
  const hasExpired = userSubscription.hasExpired;
  const hasPaidSubscription = userSubscription.creditCardRequired;
  const showCancelSubscriptionOption =
    hasSubscription &&
    hasPaidSubscription &&
    !hasExpired &&
    !hasCancelled &&
    user.role === ROLES.DENTIST;
  const showUserProfileMenuOptions =
    user.role === ROLES.DENTIST || user.role == ROLES.DOCTOR_ASSISTANT;
  // Trial: Account + Subscription Management only (moved from sidebar)
  const showAccountSubscriptionMenuOptions =
    user.role === ROLES.TRIAL_USER;
  const showReportsOption =
    (user.role === ROLES.DENTIST || user.role === ROLES.DOCTOR_ASSISTANT) &&
    !!user.officeId &&
    !!user.practiceLocationEnabled &&
    !!hasSubscription

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={user.fullName} src={user.avatar} />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.fullName}</UserBoxLabel>
            <UserBoxDescription variant="body2">{user.role}</UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{
          width: '300px', // Set a fixed width for the popover
          maxWidth: '300px', // Ensure no content overflows the width
          '& .MuiPopover-paper': {
            width: '300px', // Ensure the paper respects the popover width
            maxWidth: '300px'
          }
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={user.fullName} src={user.avatar} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.fullName}</UserBoxLabel>
            <UserBoxDescription variant="body2">{user.role}</UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        {
          <ProfileMenuComponent
            showCancelSubscriptionOption={showCancelSubscriptionOption}
            showReportsOption={showReportsOption}
            handleCancelSubscriptionModalOpen={
              handleCancelSubscriptionModalOpen
            }
            cancelSubscriptionBtnDisabled={isButtonDisabled}
            handleCloseMenu={handleClose}
            handleLogout={handleLogout}
            showUserProfileMenuOptions={showUserProfileMenuOptions}
            showAccountSubscriptionMenuOptions={
              showAccountSubscriptionMenuOptions
            }
          />
        }
        {/* {!hasCancelled && !hasExpired && hasSubscription && user.role !== ROLES.ADMIN && user.role !== ROLES.TRIAL_USER && hasPaidSubscription ?
          <Box sx={{ m: 1 }}>
            <CancelSubscriptionBtn sx={{ display: "flex", justifyContent: "flex-start" }} fullWidth onClick={handleCancelSubscriptionModalOpen} disabled={isButtonDisabled}>
              Cancel Subscription
            </CancelSubscriptionBtn>
          </Box>
          : null
        } */}
        {/* <List sx={{ p: 1 }} component="nav">
          <ListItem button to="/profile/details" component={NavLink}>
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary="My Profile" />
          </ListItem>
          <ListItem
            button
            to="/profile/settings"
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary="Account Settings" />
          </ListItem>
        </List>
        <Divider /> */}
        {/* <Box sx={{ m: 1 }}>
          <Button sx={{ display: "flex", justifyContent: "flex-start" }} color="primary" fullWidth onClick={handleLogout}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Log Out
          </Button>
        </Box> */}
      </Popover>
      {/* DEPRECATED: replaced by CancelSubscriptionFlow */}
      {/* <Box>
        <CancelSubscriptionModal
          isCancelSubscriptionModalOpen={isCancelSubscriptionModalOpen}
          handleCancelSubscriptionModalOpen={handleCancelSubscriptionModalOpen}
          cancelSubscriptionObj={cancelSubscriptionObj}
          handleCancelSubscriptionReason={handleCancelSubscriptionReason}
          handleCancelSubscription={handleCancelSubscription}
        />
      </Box> */}
      <Box>
        <CancelSubscriptionFlow
          isLoading={isButtonDisabled}
          onClose={() => setIsCancelFlowOpen(false)}
          onConfirmCancel={handleCancelSubscription}
          onConfirmPause={handlePauseSubscription}
          onNavigateToTraining={handleNavigateToTraining}
          open={isCancelFlowOpen}
          subscriptionEndDate={formatDate({
            date: userSubscription.expiredAt,
            format: DATE_FORMATS.MONTH_FIRST
          })}
        />
      </Box>
    </>
  );
}

export default HeaderUserbox;
